<template>
  <div class="history-statements-tab">
    <!-- Filtros (Competência + Tipo de Demonstrativo alinhados na mesma linha) -->
    <div class="filters-container mb-3">
      <!-- Filtro Competência -->
      <div class="form-select-group">
        <label class="select-label">Competência</label>
        <vue-monthly-picker
          class="custom-monthly-picker"
          v-model="selectedCompetence"
          :monthLabels="monthLabels"
          dateFormat="MM/YYYY"
          :max="maxDate"
          @input="onFilterChange"
        ></vue-monthly-picker>
      </div>

      <!-- Filtro Tipo de Demonstrativo -->
      <div class="form-select-group">
        <label class="select-label">Tipo de Demonstrativo</label>
        <b-form-select
          v-model="statementType"
          :options="statementTypeOptions"
          class="custom-select-small"
          @change="onFilterChange"
        ></b-form-select>
      </div>
    </div>

    <!-- Tabela -->
    <div class="table-responsive history-table-wrapper">
      <b-table
        :items="filteredItems"
        :fields="fields"
        responsive
        show-empty
        empty-text="Nenhum demonstrativo encontrado!"
        class="custom-history-table"
      >
        <template #cell(competence)="data">
          <span class="cell-competence">{{ data.item.competence }}</span>
        </template>

        <template #cell(type)="data">
          <div class="d-flex align-items-center">
            <Icon :name="getFileIconName(data.item.fileFormat)" size="20" class="mr-2" />
            <span class="cell-type-text">{{ data.item.description }}</span>
          </div>
        </template>

        <template #cell(actions)="data">
          <b-button
            variant="outline-success"
            class="btn-action-icon"
            @click="$emit('download', data.item)"
            title="Baixar Demonstrativo"
          >
            <Icon name="icon-download" size="18" />
          </b-button>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import VueMonthlyPicker from "vue-monthly-picker";
import moment from "moment";
import Icon from "../../../../components/icon/Icon.vue";

export default {
  name: "HistoryStatementsTab",
  components: {
    VueMonthlyPicker,
    Icon,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedCompetence: "",
      statementType: "Formato do Arquivo",
      maxDate: moment(),
      monthLabels: [
        "JAN", "FEV", "MAR", "ABR", "MAI", "JUN",
        "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"
      ],
      statementTypeOptions: ["Formato do Arquivo", "PDF", "CSV", "XML"],
      fields: [
        { key: "competence", label: "Competência" },
        { key: "type", label: "Tipo" },
        { key: "actions", label: "", class: "text-right" },
      ],
    };
  },
  computed: {
    filteredItems() {
      const formattedSelectedMonth = this.selectedCompetence
        ? moment(this.selectedCompetence).format("MM/YYYY")
        : "";

      return this.items.filter((item) => {
        const matchesCompetence =
          !formattedSelectedMonth || item.competence === formattedSelectedMonth;

        const matchesType =
          this.statementType === "Formato do Arquivo" ||
          item.fileFormat.toUpperCase() === this.statementType.toUpperCase();

        return matchesCompetence && matchesType;
      });
    },
  },
  methods: {
    getFileIconName(format) {
      switch (format) {
        case "pdf": return "icon-pdf";
        case "csv": return "icon-csv";
        case "xml": return "icon-xml";
        default: return "icon-pdf";
      }
    },
    onFilterChange() {
      this.$emit("filter-change", {
        competence: this.selectedCompetence
          ? moment(this.selectedCompetence).format("MM/YYYY")
          : "",
        type: this.statementType,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.filters-container {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }

  .form-select-group {
    display: flex;
    flex-direction: column;
    width: 196px;

    @media (max-width: 768px) {
      width: 100%;
    }

    .select-label {
      font-size: 16px;
      line-height: 24px;
      color: #212529;
      margin-bottom: 8px;
    }

    /* Trava a altura do Vue Monthly Picker em 38px */
    .custom-monthly-picker {
      width: 100%;

      ::v-deep .month-picker-wrapper {
        .input-uc {
          height: 38px !important;
          max-height: 38px !important;
          padding: 6px 12px !important;
          background: #ffffff !important;
          border: 1px solid #ced4da !important;
          border-radius: 4px !important;
          font-size: 14px !important;
          color: #393c40 !important;
          box-shadow: none !important;
          display: flex;
          align-items: center;
        }

        .date-popover {
          z-index: 100 !important;
        }
      }
    }

    .custom-select-small {
      height: 38px !important;
      max-height: 38px !important;
      border-radius: 4px !important;
      border: 1px solid #ced4da !important;
      font-size: 14px !important;
      color: #393c40 !important;
      padding: 6px 12px !important;
      

      text-align: left !important; 
      text-align-last: left !important; 

      
      ::v-deep option {
        text-align: left !important;
      }

      &:focus {
        box-shadow: none !important;
        border-color: #ced4da !important;
      }
    }
  }
}

.history-table-wrapper {
  .custom-history-table {
    ::v-deep th {
      background-color: #f6f7f7 !important;
      color: #212529;
      font-weight: 700;
      font-size: 14px;
      border-bottom: 1px solid #ccd4d6;
      padding: 16px 12px;
    }

    ::v-deep td {
      border-bottom: 1px solid #dfdfdf;
      padding: 12px;
      vertical-align: middle;
      height: 56px;
    }

    .cell-competence {
      font-size: 16px;
      color: #212529;
    }

    .cell-type-text {
      font-size: 14px;
      color: #1d1b20;
    }

    .btn-action-icon {
      width: 37px;
      height: 29px;
      border: 1px solid #007a4a;
      border-radius: 4px;
      color: #007a4a;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: transparent;

      &:hover {
        background-color: #007a4a;
        color: #ffffff;
      }
    }
  }
}
</style>